import React, { useState, useRef } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import { PersonnelItem } from '../../types.ts';
import { DEFAULT_PERSONNEL } from '../home/PersonnelTeamSection.tsx';
import { compressImageFile } from '../../utils/mediaOptimizer.ts';
import {
  X,
  Plus,
  Edit2,
  Trash2,
  Upload,
  Users,
  Award,
  MessageCircle,
  Mail,
  Instagram,
  Linkedin,
  Phone,
  ArrowUp,
  ArrowDown,
  Check,
  Share2,
} from 'lucide-react';

interface PersonnelManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function PersonnelManagerModal({
  isOpen,
  onClose,
}: PersonnelManagerModalProps) {
  const { settings, updateSettingsOnServer, showToast } = useApp();
  const primaryColor = settings?.theme?.primaryColor || '#059669';

  const [personnelList, setPersonnelList] = useState<PersonnelItem[]>(() => {
    return settings?.personnel && settings.personnel.length > 0
      ? settings.personnel
      : DEFAULT_PERSONNEL;
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form State
  const [formData, setFormData] = useState<Omit<PersonnelItem, 'id'>>({
    name: '',
    role: '',
    photo: '',
    description: '',
    whatsapp: '',
    phone: '',
    email: '',
    instagram: '',
    linkedin: '',
    order: 1,
  });

  if (!isOpen) return null;

  const handleOpenAddForm = () => {
    setFormData({
      name: '',
      role: '',
      photo: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=600&q=80',
      description: '',
      whatsapp: '',
      phone: '',
      email: '',
      instagram: '',
      linkedin: '',
      order: personnelList.length + 1,
    });
    setEditingId(null);
    setIsEditing(true);
  };

  const handleOpenEditForm = (person: PersonnelItem) => {
    setFormData({
      name: person.name,
      role: person.role,
      photo: person.photo,
      description: person.description,
      whatsapp: person.whatsapp || '',
      phone: person.phone || '',
      email: person.email || '',
      instagram: person.instagram || '',
      linkedin: person.linkedin || '',
      order: person.order || 1,
    });
    setEditingId(person.id);
    setIsEditing(true);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const compressed = await compressImageFile(file, 800, 800, 0.85);
      setFormData((prev) => ({ ...prev, photo: compressed.dataUrl }));
      showToast('Foto personel berhasil diunggah & dioptimasi!', 'success');
    } catch (err) {
      showToast('Gagal memproses foto', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSavePerson = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      showToast('Nama personel wajib diisi!', 'warning');
      return;
    }
    if (!formData.role.trim()) {
      showToast('Jabatan / Posisi personel wajib diisi!', 'warning');
      return;
    }

    let updated: PersonnelItem[];
    if (editingId) {
      // Edit
      updated = personnelList.map((p) =>
        p.id === editingId ? { ...formData, id: editingId } : p
      );
      showToast(`Data personel ${formData.name} berhasil diperbarui!`, 'success');
    } else {
      // Add
      const newPerson: PersonnelItem = {
        ...formData,
        id: `pers-${Date.now()}-${Math.random().toString(36).substring(2, 5)}`,
      };
      updated = [...personnelList, newPerson];
      showToast(`Personel baru ${formData.name} berhasil ditambahkan!`, 'success');
    }

    // Sort by order
    updated.sort((a, b) => (a.order || 0) - (b.order || 0));
    setPersonnelList(updated);
    await updateSettingsOnServer({ personnel: updated });
    setIsEditing(false);
    setEditingId(null);
  };

  const handleDeletePerson = async (id: string, name: string) => {
    const updated = personnelList.filter((p) => p.id !== id);
    setPersonnelList(updated);
    await updateSettingsOnServer({ personnel: updated });
    showToast(`Personel ${name} berhasil dihapus.`, 'info');
  };

  const handleMoveOrder = async (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === personnelList.length - 1)
    ) {
      return;
    }
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    const updated = [...personnelList];
    const temp = updated[index];
    updated[index] = updated[targetIndex];
    updated[targetIndex] = temp;

    // Re-assign order numbers
    updated.forEach((p, idx) => {
      p.order = idx + 1;
    });

    setPersonnelList(updated);
    await updateSettingsOnServer({ personnel: updated });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-4xl rounded-3xl shadow-2xl border border-slate-100 overflow-hidden flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-2xl flex items-center justify-center text-white shadow-md shrink-0"
              style={{ backgroundColor: primaryColor }}
            >
              <Users className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-extrabold text-sm sm:text-base text-slate-900 flex items-center gap-2">
                <span>Kelola Daftar Personel & Tim Ahli</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                  {personnelList.length} Personel
                </span>
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Tambahkan, edit data, dan atur susunan personel tim Anda yang tampil di beranda web.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-slate-200 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-6 flex-1">
          {/* If In Form Mode */}
          {isEditing ? (
            <form onSubmit={handleSavePerson} className="space-y-5 bg-slate-50 p-5 rounded-2xl border border-slate-200 animate-in zoom-in-98 duration-150">
              <div className="flex items-center justify-between border-b border-slate-200/80 pb-3">
                <h4 className="font-bold text-sm text-slate-900 flex items-center gap-2">
                  <Award className="w-4 h-4 text-emerald-600" />
                  <span>{editingId ? 'Edit Data Personel' : 'Tambah Personel Baru'}</span>
                </h4>
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="text-xs text-slate-500 hover:text-slate-800 font-semibold cursor-pointer"
                >
                  Batal
                </button>
              </div>

              {/* Photo Input & Preview */}
              <div className="flex flex-col sm:flex-row items-center gap-5">
                <div className="w-24 h-28 sm:w-28 sm:h-32 rounded-2xl overflow-hidden bg-slate-200 border-2 border-slate-300 relative shrink-0 shadow-inner">
                  {formData.photo ? (
                    <img
                      src={formData.photo}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
                      <Users className="w-8 h-8" />
                      <span className="text-[10px] mt-1">Belum ada foto</span>
                    </div>
                  )}
                  {isUploading && (
                    <div className="absolute inset-0 bg-slate-900/60 flex items-center justify-center text-white text-xs font-bold">
                      Mengompres...
                    </div>
                  )}
                </div>

                <div className="flex-1 space-y-2 w-full">
                  <label className="block text-xs font-bold text-slate-800">
                    Foto Personel (Upload atau URL Gambar)
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="px-3.5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs flex items-center gap-1.5 transition-colors cursor-pointer shrink-0 shadow-xs"
                    >
                      <Upload className="w-3.5 h-3.5" />
                      <span>Upload Foto</span>
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <input
                      type="text"
                      placeholder="Atau tempel URL gambar (https://...)"
                      value={formData.photo}
                      onChange={(e) => setFormData({ ...formData, photo: e.target.value })}
                      className="w-full text-xs bg-white border border-slate-300 px-3 py-2 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
                    />
                  </div>
                  <p className="text-[10px] text-slate-400">
                    Foto otomatis dikompres ke format WebP berkualitas tinggi agar website tetap cepat dibuka.
                  </p>
                </div>
              </div>

              {/* Name & Role */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-800 mb-1">
                    Nama Lengkap & Gelar *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Contoh: Budi Santoso, S.Kom"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full text-xs font-bold text-slate-900 bg-white border border-slate-300 px-3.5 py-2.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-800 mb-1">
                    Jabatan / Posisi / Keahlian *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Contoh: Senior Project Manager & Konsultan"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full text-xs font-semibold text-slate-900 bg-white border border-slate-300 px-3.5 py-2.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-bold text-slate-800 mb-1">
                  Deskripsi Singkat / Profil Pengalaman
                </label>
                <textarea
                  rows={3}
                  placeholder="Contoh: Berpengalaman lebih dari 8 tahun dalam manajemen proyek, konsultasi profesional, dan layanan klien..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full text-xs text-slate-800 bg-white border border-slate-300 p-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 leading-relaxed"
                />
              </div>

              {/* Contact & Social Links */}
              <div className="space-y-2 pt-2 border-t border-slate-200/80">
                <label className="block text-xs font-bold text-slate-800 flex items-center gap-1.5">
                  <Share2 className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Kontak & Media Sosial Personel (Opsional)</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div>
                    <span className="text-[10px] font-semibold text-slate-500 block mb-1">WhatsApp</span>
                    <div className="relative">
                      <MessageCircle className="w-3.5 h-3.5 text-emerald-600 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="628123456789"
                        value={formData.whatsapp || ''}
                        onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                        className="w-full text-xs bg-white border border-slate-300 pl-8 pr-2.5 py-1.5 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>
                  <div>
                    <span className="text-[10px] font-semibold text-slate-500 block mb-1">Email</span>
                    <div className="relative">
                      <Mail className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="email"
                        placeholder="nama@email.com"
                        value={formData.email || ''}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full text-xs bg-white border border-slate-300 pl-8 pr-2.5 py-1.5 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>
                  <div>
                    <span className="text-[10px] font-semibold text-slate-500 block mb-1">Instagram</span>
                    <div className="relative">
                      <Instagram className="w-3.5 h-3.5 text-pink-600 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="username"
                        value={formData.instagram || ''}
                        onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                        className="w-full text-xs bg-white border border-slate-300 pl-8 pr-2.5 py-1.5 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>
                  <div>
                    <span className="text-[10px] font-semibold text-slate-500 block mb-1">LinkedIn</span>
                    <div className="relative">
                      <Linkedin className="w-3.5 h-3.5 text-sky-700 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="username"
                        value={formData.linkedin || ''}
                        onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                        className="w-full text-xs bg-white border border-slate-300 pl-8 pr-2.5 py-1.5 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-200 text-xs font-bold transition-colors cursor-pointer"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl text-white font-bold text-xs shadow-md flex items-center gap-1.5 transition-all cursor-pointer"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Check className="w-4 h-4" />
                  <span>Simpan Data Personel</span>
                </button>
              </div>
            </form>
          ) : (
            <>
              {/* Top Action Bar */}
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-700">
                  Daftar Personel Saat Ini ({personnelList.length})
                </span>
                <button
                  type="button"
                  onClick={handleOpenAddForm}
                  className="px-4 py-2 rounded-xl text-white font-bold text-xs shadow-md flex items-center gap-1.5 transition-all cursor-pointer"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Plus className="w-4 h-4" />
                  <span>+ Tambah Personel Baru</span>
                </button>
              </div>

              {/* Personnel List Items */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {personnelList.map((person, index) => (
                  <div
                    key={person.id}
                    className="p-4 bg-white rounded-2xl border border-slate-200 shadow-2xs hover:border-emerald-300 transition-all flex items-start gap-3.5 group"
                  >
                    {/* Photo */}
                    <img
                      src={person.photo}
                      alt={person.name}
                      className="w-16 h-20 rounded-xl object-cover border border-slate-200 shrink-0 shadow-xs"
                    />

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-1">
                        <div>
                          <span
                            className="px-2 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wider text-white inline-block mb-1"
                            style={{ backgroundColor: primaryColor }}
                          >
                            {person.role}
                          </span>
                          <h4 className="font-bold text-sm text-slate-900 truncate">
                            {person.name}
                          </h4>
                        </div>

                        {/* Reorder Buttons */}
                        <div className="flex items-center gap-0.5 opacity-60 group-hover:opacity-100">
                          <button
                            type="button"
                            disabled={index === 0}
                            onClick={() => handleMoveOrder(index, 'up')}
                            className="p-1 rounded text-slate-400 hover:text-slate-800 disabled:opacity-20 cursor-pointer"
                            title="Pindah ke kiri/atas"
                          >
                            <ArrowUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            disabled={index === personnelList.length - 1}
                            onClick={() => handleMoveOrder(index, 'down')}
                            className="p-1 rounded text-slate-400 hover:text-slate-800 disabled:opacity-20 cursor-pointer"
                            title="Pindah ke kanan/bawah"
                          >
                            <ArrowDown className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      <p className="text-xs text-slate-500 line-clamp-2 mt-1 leading-relaxed">
                        {person.description}
                      </p>

                      {/* Action buttons */}
                      <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100 text-xs">
                        <div className="flex items-center gap-1.5 text-slate-400">
                          {person.whatsapp && <MessageCircle className="w-3.5 h-3.5 text-emerald-600" />}
                          {person.email && <Mail className="w-3.5 h-3.5 text-slate-500" />}
                          {person.instagram && <Instagram className="w-3.5 h-3.5 text-pink-600" />}
                          {person.linkedin && <Linkedin className="w-3.5 h-3.5 text-sky-700" />}
                        </div>

                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => handleOpenEditForm(person)}
                            className="px-2.5 py-1 bg-slate-100 hover:bg-emerald-50 hover:text-emerald-700 text-slate-700 font-bold rounded-lg text-[11px] flex items-center gap-1 transition-colors cursor-pointer"
                          >
                            <Edit2 className="w-3 h-3" />
                            <span>Edit</span>
                          </button>
                          {deleteConfirmId === person.id ? (
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => {
                                  handleDeletePerson(person.id, person.name);
                                  setDeleteConfirmId(null);
                                }}
                                className="px-2 py-1 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-lg text-[10px] cursor-pointer"
                              >
                                Hapus?
                              </button>
                              <button
                                type="button"
                                onClick={() => setDeleteConfirmId(null)}
                                className="px-2 py-1 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold rounded-lg text-[10px] cursor-pointer"
                              >
                                Batal
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setDeleteConfirmId(person.id)}
                              className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                              title="Hapus Personel"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {personnelList.length === 0 && (
                <div className="p-8 text-center text-slate-400 bg-slate-50 rounded-2xl border border-dashed border-slate-300">
                  <Users className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p className="font-bold text-slate-600 text-sm">Belum ada daftar personel</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Klik tombol "+ Tambah Personel Baru" di atas untuk menambahkan tim Anda.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
          <p className="text-[11px] text-slate-500">
            *Semua perubahan personel otomatis tersinkronisasi ke tampilan beranda web.
          </p>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition-colors cursor-pointer"
          >
            Selesai
          </button>
        </div>
      </div>
    </div>
  );
}
